/**
 * Homepage — Premium editorial landing page.
 *
 * Sections:
 * 1. Hero — Large full-bleed image with refined typography
 * 2. Marquee — Trust signals
 * 3. Collections — Grid of curated collections
 * 4. BestSellers — Top 3 products
 * 5. RoomInspiration — Editorial room shots
 * 6. WhyArtificial — Value propositions
 * 7. Lifestyle — Full-bleed quote image
 * 8. Testimonials — Customer quotes
 * 9. Newsletter — Email signup CTA
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, useMemo } from "react";
import { Sprout, SlidersHorizontal } from "lucide-react";
import { ProductCard } from "@/features/products/components/product-card";
import { ProductGrid } from "@/features/products/components/product-grid";
import { SearchInput } from "@/components/forms/search-input";
import { EmptyState } from "@/components/common/empty-state";
import { Reveal } from "@/components/common/reveal";
import { siteConfig } from "@/config/site";
import { useLiveProducts } from "@/features/products/hooks/use-live-products";
import { useLiveCategories } from "@/features/categories/hooks/use-live-categories";
import { InteractiveHero } from "@/components/home/interactive-hero";
import { LuxuryCategorySlider } from "@/components/common/LuxuryCategorySlider";
import { PromotionalPosterBanner } from "@/components/common/PromotionalPosterBanner";

import hero from "@/assets/hero.jpg";
import roomDining from "@/assets/room-dining.jpg";
import roomOffice from "@/assets/room-office.jpg";
import greenwall from "@/assets/greenwall.jpg";

import { WatchAndBuySection } from "@/components/home/WatchAndBuySection";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: siteConfig.seo.defaultTitle },
      { name: "description", content: siteConfig.seo.defaultDescription },
      // Accurate here: this route serves the default 1200x630 og-image.jpg. The hint
      // lives with the image it describes rather than at root -- see __root.tsx.
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:title", content: siteConfig.seo.defaultTitle },
      {
        property: "og:description",
        content: siteConfig.seo.defaultDescription,
      },
    ],
    links: [
      { rel: "canonical", href: siteConfig.url },
      // Preload the LCP candidates so the browser fetches them during HTML parsing.
      // hero-slide-monstera.webp = the first slide shown in the Interactive Hero
      {
        rel: "preload",
        as: "image",
        href: "/hero-slide-monstera.webp",
        fetchPriority: "high",
      },
      {
        rel: "preload",
        as: "image",
        href: "/banners/banner-living-room.webp",
        fetchPriority: "high",
      },
    ],
  }),
  component: Index,
});

function Index() {
  return (
    <>
      <InteractiveHero />
      <SectionDivider />
      {/* Promotional Poster Banner between Hero & Category Slider */}
      <PromotionalPosterBanner />
      <FullShopSection />
      <BestSellers />
      <WatchAndBuySection />
      <RoomInspiration />
      <WhyArtificial />
      <Lifestyle />
      <Testimonials />
      <Newsletter />
    </>
  );
}

/* ────────────────────────────────────────────────────────────
   HERO — Clean, Apple-style hero with large image and
   refined typography. No animated doors or falling petals.
   The product sells through restraint.
   ──────────────────────────────────────────────────────────── */

function Hero() {
  const [y, setY] = useState(0);

  useEffect(() => {
    const onScroll = () => setY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section className="relative min-h-[95svh] overflow-hidden md:min-h-screen bg-stone-950">
      {/* Background image with parallax */}
      <div className="absolute inset-0 z-0">
        <img
          src={hero}
          alt="Sunlit botanical sanctuary with lush green plants"
          width={1408}
          height={1760}
          className="h-full w-full object-cover object-[62%_center]"
          style={{ transform: `translate3d(0, ${y * 0.1}px, 0)` }}
          fetchPriority="high"
        />
        {/* Gradient overlays for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-background/85 via-background/40 to-transparent z-10" />
        <div className="absolute inset-0 bg-gradient-to-t from-background/70 via-transparent to-transparent z-10" />
      </div>

      {/* Content */}
      <div className="relative z-20 section-container flex min-h-[95svh] flex-col justify-end pb-20 pt-32 md:min-h-screen md:pb-28">
        <div className="max-w-[48rem]">
          <div className="flex items-center gap-3">
            <span className="eyebrow flex items-center gap-2">
              <Sprout className="h-3.5 w-3.5 text-sage" />
              New Collection — Monsoon '26
            </span>
          </div>

          {/* The display line carries the brand; the span carries the search term. Both sit
              inside the h1 so the page's single most weighted heading is not spent entirely
              on a phrase nobody searches for. Styling keeps the hero visually dominant. */}
          <h1 className="mt-6 font-display text-[clamp(3rem,8vw,6.5rem)] leading-[0.92] tracking-tight">
            Bring <em className="italic text-sage">Nature</em>
            <br />
            Home.
            <span className="mt-5 block text-[clamp(1.05rem,2.1vw,1.7rem)] font-normal leading-snug tracking-normal text-muted-foreground">
              Artificial flowers &amp; plants for home decoration
            </span>
          </h1>

          <p className="mt-8 max-w-lg text-base leading-relaxed text-muted-foreground">
            Handcrafted silk flower bushes, faux bonsai trees and artificial topiary — small potted
            pieces, every one with its pot included. Designed to bring calm to every room, without
            the watering.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-8">
            <Link
              to="/shop"
              className="inline-flex min-h-12 items-center bg-primary px-10 text-xs tracking-[0.2em] text-primary-foreground uppercase transition-opacity duration-300 hover:opacity-90 shadow-sm"
            >
              Shop the Collection
            </Link>
            <Link to="/collections" className="rule-link text-sm">
              View Lookbook
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 right-6 z-20 hidden items-center gap-3 md:flex">
        <span className="eyebrow">Scroll</span>
        <span className="block h-12 w-px bg-foreground/25">
          <span className="block h-4 w-px animate-[pulse_2.4s_ease-in-out_infinite] bg-foreground/70" />
        </span>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   MARQUEE — Trust signals / USPs
   ──────────────────────────────────────────────────────────── */

function SectionDivider() {
  return <div className="w-full border-t-2 border-stone-300/80 my-2" aria-hidden="true" />;
}

/* ────────────────────────────────────────────────────────────
   COLLECTIONS — Grid of curated collections
   ──────────────────────────────────────────────────────────── */

function Collections() {
  const { categories } = useLiveCategories();
  const { products: liveProducts } = useLiveProducts();

  const collectionItems = categories
    .filter((c) => (c.status || "ACTIVE") === "ACTIVE")
    .map((c) => {
      const matchingProducts = liveProducts.filter(
        (p) => (p.category || "").toLowerCase().trim() === (c.name || "").toLowerCase().trim(),
      );
      return {
        title: c.name || "Botanicals",
        count: matchingProducts.length,
        image: c.image || matchingProducts[0]?.image || "/plantpicture/p1/5.webp",
      };
    });

  return (
    <section className="section-container py-10 md:py-14">
      <div className="flex items-end justify-between gap-4 border-b border-stone-200/80 pb-4">
        <Reveal>
          <h2 className="font-display text-2xl md:text-4xl font-bold text-foreground">
            Curated collections to bring the outside in
          </h2>
        </Reveal>
        <Reveal delay={100}>
          <Link
            to="/collections"
            className="rule-link text-xs md:text-sm font-semibold text-emerald-800 uppercase tracking-wider"
          >
            All collections &rarr;
          </Link>
        </Reveal>
      </div>

      <div className="mt-6 md:mt-8 grid gap-x-5 gap-y-6 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
        {collectionItems.map((c, i) => (
          <Reveal key={c.title} delay={i * 60}>
            <Link to="/shop" search={{ category: c.title }} className="group block">
              <div className="overflow-hidden rounded-2xl bg-stone border border-stone-200/60 shadow-sm">
                <img
                  src={c.image}
                  srcSet={`${c.image.replace('.webp', '-sm.webp')} 400w, ${c.image} 900w`}
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                  alt={c.title}
                  loading="lazy"
                  width={900}
                  height={1100}
                  className="aspect-[4/4.5] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="mt-3 flex items-baseline justify-between">
                <h3 className="text-sm md:text-lg font-display font-bold text-foreground group-hover:text-emerald-800 transition-colors">
                  {c.title}
                </h3>
                <span className="text-xs font-semibold text-stone-500 tabular-nums">
                  {c.count} items
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   BEST SELLERS — Top 3 products
   ──────────────────────────────────────────────────────────── */

function BestSellers() {
  const { products: productList } = useLiveProducts();

  const bestSellers = useMemo(() => {
    const featured = productList.filter(
      (p) =>
        p.badge === "Best Seller" ||
        p.badge === "Featured" ||
        (p as { is_featured?: boolean }).is_featured,
    );
    if (featured.length >= 3) return featured.slice(0, 3);
    return productList.slice(0, 3);
  }, [productList]);

  return (
    <section className="border-t bg-secondary/25">
      <div className="section-container section-padding">
        <Reveal>
          <p className="eyebrow">Most loved</p>
          <h2 className="mt-4">Bestselling botanicals</h2>
        </Reveal>
        <div className="mt-14 grid gap-x-6 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
          {bestSellers.map((p, i) => (
            <Reveal key={p.id} delay={i * 90}>
              <ProductCard product={p} />
            </Reveal>
          ))}
        </div>
        <Reveal delay={200}>
          <div className="mt-14 text-center">
            <Link
              to="/shop"
              className="inline-flex min-h-12 items-center border border-border px-10 text-xs tracking-[0.2em] uppercase transition-colors hover:bg-primary hover:text-primary-foreground hover:border-primary"
            >
              View all products
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   ROOM INSPIRATION — Editorial imagery
   ──────────────────────────────────────────────────────────── */

function RoomInspiration() {
  return (
    <section className="section-container section-padding">
      <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
        <Reveal className="lg:col-span-7">
          <figure className="overflow-hidden">
            <img
              src={roomDining}
              alt="Dining nook styled with artificial eucalyptus stems in a ceramic vase"
              loading="lazy"
              width={1400}
              height={1000}
              className="w-full object-cover"
            />
          </figure>
        </Reveal>
        <div className="lg:col-span-4 lg:col-start-9 lg:self-center">
          <Reveal delay={120}>
            <p className="eyebrow">Room inspiration</p>
            <h2 className="mt-6">A room styled in an afternoon, kept for a decade</h2>
            <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
              Our stylists build each arrangement around light, texture and the way a room is
              actually lived in — then we ship it as one considered set.
            </p>
            <Link to="/collections" className="rule-link mt-8 inline-block text-sm">
              See the styling notes
            </Link>
          </Reveal>
        </div>
      </div>

      <div className="mt-20 grid gap-6 lg:grid-cols-12">
        <Reveal className="lg:col-span-5 lg:col-start-2">
          <img
            src={roomOffice}
            alt="Minimal home office corner with a tall artificial palm"
            loading="lazy"
            width={1000}
            height={1200}
            className="w-full object-cover"
          />
          <p className="mt-4 text-xs text-muted-foreground">Study, Mumbai</p>
        </Reveal>
        <Reveal delay={120} className="lg:col-span-5 lg:self-end">
          <img
            src={greenwall}
            alt="Preserved moss wall behind a wooden bench"
            loading="lazy"
            width={1400}
            height={1000}
            className="w-full object-cover"
          />
          <p className="mt-4 text-xs text-muted-foreground">Café interior, Bengaluru</p>
        </Reveal>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   WHY ARTIFICIAL — Value propositions
   ──────────────────────────────────────────────────────────── */

function WhyArtificial() {
  const points = [
    ["01", "No light required", "Corners, hallways and north-facing rooms finally get greenery."],
    ["02", "Allergy friendly", "No pollen, no soil, no fuss for sensitive households."],
    ["03", "A decade of colour", "UV-stable pigments tested against ten years of daylight."],
    ["04", "Five minutes a year", "A soft brush is the entire care routine."],
  ];

  return (
    <section className="border-y">
      <div className="section-container grid gap-y-14 py-20 md:grid-cols-[0.9fr_1.4fr] md:gap-x-20 md:py-28">
        <Reveal>
          <h2>
            Why artificial,
            <span className="block italic">done properly</span>
          </h2>
        </Reveal>
        <ul className="grid gap-x-14 gap-y-12 sm:grid-cols-2">
          {points.map(([n, t, d], i) => (
            <Reveal as="li" key={n} delay={i * 80}>
              <span className="font-display text-sm text-accent">{n}</span>
              <h3 className="mt-3 text-lg">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{d}</p>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   LIFESTYLE — Full-bleed quote image
   ──────────────────────────────────────────────────────────── */

function Lifestyle() {
  return (
    <section className="relative">
      <img
        src={greenwall}
        alt="Moss wall installation in a sunlit space"
        loading="lazy"
        width={1400}
        height={1000}
        className="h-[60vh] w-full object-cover md:h-[78vh]"
      />
      <div className="absolute inset-0 bg-foreground/25" />
      <div className="absolute inset-0 section-container flex items-end pb-16 md:pb-28">
        <Reveal>
          <p className="max-w-2xl font-display text-[clamp(1.6rem,3.4vw,3rem)] leading-[1.12] text-background">
            "We wanted the calm of a garden, in a flat with two north windows."
          </p>
          <p className="mt-6 text-xs tracking-[0.24em] text-background/80 uppercase">
            Green wall — 3.2 m, private residence, Pune
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   TESTIMONIALS — Customer quotes
   ──────────────────────────────────────────────────────────── */

function Testimonials() {
  const quotes = [
    ["Priya M.", "Three months in and guests still reach out to touch the leaves."],
    ["Rahul T.", "The olive tree turned a dead hallway corner into the best spot in the flat."],
    ["Ananya R.", "Packaging, weight, finish — everything felt like furniture, not decor."],
  ];

  return (
    <section className="section-container section-padding">
      <Reveal>
        <p className="eyebrow text-center">What our customers say</p>
      </Reveal>
      <div className="mt-12 grid gap-12 md:grid-cols-3 md:gap-16">
        {quotes.map(([name, quote], i) => (
          <Reveal key={name} delay={i * 100} className={i === 1 ? "md:mt-14" : ""}>
            <blockquote>
              <p className="font-display text-xl leading-relaxed">"{quote}"</p>
              <footer className="eyebrow mt-6">{name}</footer>
            </blockquote>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   NEWSLETTER — Email signup CTA
   ──────────────────────────────────────────────────────────── */

function Newsletter() {
  return (
    <section className="border-t bg-primary text-primary-foreground">
      <div className="section-container grid gap-10 py-20 md:grid-cols-2 md:py-24">
        <h2>
          Slow letters,
          <span className="block italic">four times a year</span>
        </h2>
        <form
          onSubmit={(e) => e.preventDefault()}
          className="self-end"
          aria-label="Newsletter signup"
        >
          <label htmlFor="newsletter-email" className="sr-only">
            Email address
          </label>
          <div className="flex items-center gap-4 border-b border-primary-foreground/40 pb-3">
            <input
              id="newsletter-email"
              type="email"
              required
              placeholder="your@email.com"
              className="min-h-11 w-full bg-transparent text-sm outline-none placeholder:text-primary-foreground"
              autoComplete="email"
            />
            <button
              type="submit"
              className="shrink-0 text-xs tracking-[0.2em] uppercase transition-opacity hover:opacity-80"
            >
              Subscribe
            </button>
          </div>
          <p className="mt-4 text-xs text-primary-foreground/70">
            Seasonal styling notes. No noise. Unsubscribe anytime.
          </p>
        </form>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   FULL SHOP SECTION — Embedded shop catalogue under homepage
   ──────────────────────────────────────────────────────────── */

type SortOption = "featured" | "price-low" | "price-high" | "rating";

function FullShopSection() {
  const { products: liveProductsList } = useLiveProducts();
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>("featured");
  const [displayCount, setDisplayCount] = useState(12);

  const categories = ["All", ...Array.from(new Set(liveProductsList.map((p) => p.category)))];

  const filteredProducts = useMemo(() => {
    let list =
      activeCategory === "All"
        ? liveProductsList
        : liveProductsList.filter(
            (p) => (p.category || "").toLowerCase().trim() === activeCategory.toLowerCase().trim(),
          );

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (p) =>
          (p.name || "").toLowerCase().includes(q) ||
          (p.category || "").toLowerCase().includes(q) ||
          (p.note || "").toLowerCase().includes(q) ||
          (p.description || "").toLowerCase().includes(q),
      );
    }

    switch (sortBy) {
      case "price-low":
        return [...list].sort((a, b) => a.price - b.price);
      case "price-high":
        return [...list].sort((a, b) => b.price - a.price);
      case "rating":
        return [...list].sort((a, b) => (b.rating || 0) - (a.rating || 0));
      default:
        return list;
    }
  }, [liveProductsList, activeCategory, searchQuery, sortBy]);

  // Reset display count when filters change
  useEffect(() => {
    setDisplayCount(12);
  }, [activeCategory, searchQuery, sortBy]);

  const currentProducts = filteredProducts.slice(0, displayCount);

  return (
    <section className="section-container py-10 md:py-14 border-t border-stone-200">
      {/* Controls bar */}
      <div className="flex flex-row items-center gap-3 md:justify-between">
        <div className="flex-1 max-w-md">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search botanicals, styles or pots…"
          />
        </div>

        <div className="flex items-center shrink-0">
          <label htmlFor="homepage-sort-select" className="sr-only">
            Sort products
          </label>
          <select
            id="homepage-sort-select"
            aria-label="Sort products"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="h-11 sm:h-12 rounded-full border border-stone-300/80 bg-white text-stone-900 px-3 sm:px-5 text-xs sm:text-sm font-semibold shadow-xs outline-none transition-[border-color,box-shadow] duration-200 hover:border-stone-400 focus-visible:border-emerald-800 focus-visible:ring-2 focus-visible:ring-emerald-800/15 cursor-pointer dark:bg-stone-900 dark:text-stone-100"
          >
            <option value="featured">Sort by: Featured</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="rating">Highest Rated</option>
          </select>
        </div>
      </div>

      {/* Luxury 3D Cutout Category Slider Bar (Image 1 style) */}
      <LuxuryCategorySlider
        activeCategory={activeCategory}
        onSelectCategory={setActiveCategory}
        className="my-6"
      />

      {/* Product Results */}
      {filteredProducts.length === 0 ? (
        <EmptyState
          title="No botanicals found"
          description="Try resetting your search query or choosing another category."
        >
          <button
            onClick={() => {
              setSearchQuery("");
              setActiveCategory("All");
            }}
            className="mt-4 text-xs text-emerald-800 underline font-semibold transition-colors hover:text-emerald-950 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-800"
          >
            Clear all filters
          </button>
        </EmptyState>
      ) : (
        <div className="mt-12">
          <Reveal>
            <ProductGrid products={currentProducts} priorityCount={2} />
          </Reveal>
          
          {displayCount < filteredProducts.length && (
            <div className="mt-12 flex justify-center">
              <button 
                onClick={() => setDisplayCount(prev => prev + 12)}
                className="px-8 py-3 rounded-full border border-stone-300 text-sm font-semibold hover:bg-stone-50 transition-colors cursor-pointer active:scale-95 shadow-sm"
              >
                Load More Botanicals
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
